import { DEFAULT_LOCALE } from '@constants/locales';
import { FETCH_LIMIT_RECOMENDATIONS, SINGLE_PAGE } from '@constants/pagination';
import { QUERY_KEY } from '@constants/queries';
import { fetchNews, useFetchNews } from '@hooks/queries/useFetchNews';
import {
  fetchNewsBySlug,
  useFetchNewsBySlug,
} from '@hooks/queries/useFetchNewsSlug';
import {
  fetchPublicity,
  useFetchPublicity,
} from '@hooks/queries/useFetchPublicity';
import { Box, Stack } from '@mantine/core';
import { EmptyStateComponent } from '@molecules/frontoffice/EmptyStateComponent';
import { LoadingStateComponent } from '@molecules/frontoffice/LoadingStateComponent';
import { NewsDetails } from '@molecules/frontoffice/NewsDetails';
import { getPublicNewsBySlug } from '@services/news';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { validateZodSchema } from '@utils/zodSchemas';
import { newsDetailsPageZodSchema } from '@utils/zodSchemas/details';
import { GetServerSideProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { ShareMenu } from '@molecules/ShareMenu';
import { useDisclosure } from '@mantine/hooks';
import { useState } from 'react';
import { RecommendedNews } from '@molecules/frontoffice/RecommendedNews';
import { SEO } from '@organisms/SEO';
import { fetchCategories } from '@hooks/queries/useFetchCategories';
import { fetchPublicConfiguration } from '@hooks/queries/useFetchPublicConfiguration';
import { RandomPublicityComponent } from '@organisms/RandomPublicityComponent';

export const getServerSideProps: GetServerSideProps = async ({ ...ctx }) => {
  const parsedQuery = validateZodSchema(newsDetailsPageZodSchema, ctx.params);
  const { data: parsedData } = parsedQuery;

  const params = {
    slug: parsedData?.slug ?? '',
  };

  const paramsPublicity = {
    fetchAll: true,
  };

  const paramsLastNews = {
    page: SINGLE_PAGE,
    limit: FETCH_LIMIT_RECOMENDATIONS,
  };

  const queryClient = new QueryClient();

  const news = await getPublicNewsBySlug(params.slug);

  if (!news || !news.id) {
    return {
      notFound: true,
    };
  }

  const paramsRecommendations = {
    category: news?.category.id ?? '',
    page: SINGLE_PAGE,
    limit: FETCH_LIMIT_RECOMENDATIONS,
  };

  const recomendationNewsPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_NEWS, paramsRecommendations],
    queryFn: async () => {
      return fetchNews(paramsRecommendations);
    },
  });

  const lastNewsPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_NEWS, paramsLastNews],
    queryFn: async () => {
      return fetchNews(paramsLastNews);
    },
  });

  const newsSlugPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_NEWS_BY_SLUG, params.slug],
    queryFn: async () => {
      const data = await fetchNewsBySlug(params.slug);
      return data;
    },
  });

  const publicityPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_PUBLICITY, paramsPublicity],
    queryFn: async () => {
      return await fetchPublicity(paramsPublicity);
    },
  });

  const categoriesPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_CATEGORIES, { fetchAll: true }],
    queryFn: async () => {
      return await fetchCategories({ fetchAll: true });
    },
  });

  const configurationsPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_PUBLIC_CONFIGURATION],
    queryFn: async () => {
      return await fetchPublicConfiguration();
    },
  });

  await Promise.all([
    categoriesPromise,
    publicityPromise,
    configurationsPromise,
    newsSlugPromise,
    lastNewsPromise,
    recomendationNewsPromise,
  ]);

  return {
    props: {
      ...(await serverSideTranslations(ctx.locale ?? DEFAULT_LOCALE, [
        'common',
        'dashboard',
      ])),
      dehydratedState: dehydrate(queryClient),
      slug: parsedData?.slug ?? '',
    },
  };
};

const NewsDetailsPage = ({ slug }: { slug: string }) => {
  const { data: news, isFetched, isFetching } = useFetchNewsBySlug(slug);

  const [
    isShareModalOpen,
    { open: onOpenShareModal, close: onCloseShareModal },
  ] = useDisclosure(false);

  const [shareLink, setShareLink] = useState<string>();

  const { data: recommendationNews } = useFetchNews({
    category: news?.category.id,
    page: SINGLE_PAGE,
    limit: FETCH_LIMIT_RECOMENDATIONS,
  });

  const { data: lastNews } = useFetchNews({
    page: SINGLE_PAGE,
    limit: FETCH_LIMIT_RECOMENDATIONS,
  });

  const handleShareNews = () => {
    setShareLink(
      `${process.env.NEXT_PUBLIC_APP_URL}/noticias/${news?.category.slug}/${news?.slug}`
    );
    onOpenShareModal();
  };

  const { data: publicity } = useFetchPublicity({
    fetchAll: true,
  });

  if ((isFetching && !news) || (!isFetched && !news)) {
    return <LoadingStateComponent />;
  }

  if ((isFetched && !news) || !news) {
    return <EmptyStateComponent />;
  }

  const hasBannerPublicity =
    publicity?.data && publicity?.data.some((pub) => pub.type === 'BANNER');

  return (
    <>
      <SEO
        title={news.title_seo ?? news.title}
        description={news?.description_seo}
        keywords={news.keywords_seo}
        image_url={news?.header_image?.key}
        type="article"
        post={{
          title: news.title_seo ?? news.title,
          description: news.description_seo,
          mainImageUrl: news?.header_image?.key,
          publishedAt: news.publish_at,
        }}
      />
      <Stack>
        {hasBannerPublicity && (
          <Box
            display="flex"
            mb={20}
            sx={(theme) => ({
              width: '100vw',
              marginLeft: 'calc(50% - 50vw)',
              [`@media (min-width: ${theme.breakpoints.md})`]: {
                width: '100%',
                marginLeft: 0,
              },
            })}
          >
            <RandomPublicityComponent
              index={0}
              type="BANNER"
              publicities={publicity?.data ?? []}
            />
          </Box>
        )}
        {news && (
          <NewsDetails
            category={news?.category}
            publicities={publicity?.data}
            date={news.publish_at}
            episode={news?.spotify_url}
            title={news.title}
            subTitle={news?.sub_title}
            headerImage={news?.header_image?.key}
            description={news.description}
            author={news.author}
            handleShareNews={handleShareNews}
            lastNews={lastNews?.data}
            isLastNewsLoading={!lastNews}
          />
        )}
        <RecommendedNews
          recommendedNews={
            recommendationNews?.data
              .filter((item) => item.slug !== news.slug)
              .slice(0, 4) ?? []
          }
          redirectPath={`/noticias/${news.category.slug}`}
          title="common.recommendations"
        />
        <ShareMenu
          isModalOpen={isShareModalOpen}
          onCloseModal={onCloseShareModal}
          link={shareLink}
          title={news.title}
        />
      </Stack>
    </>
  );
};

export default NewsDetailsPage;
